import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  BulkCreateProjectManagementPlannedRecordDocument,
  BulkCreateProjectManagementPlannedRecordMutationVariables,
  ContributorsScheduleDocument,
  DeleteCustomRangeProjectManagementPlannedRecordDocument,
  DeleteProjectManagementPlannedRecordDocument,
  ErrorDetailsFragment,
  ProjectForSelectFragment,
  SubprojectForSelectFragment,
  UpdateCustomRangeProjectManagementPlannedRecordDocument,
  UpdateProjectManagementPlannedRecordDocument,
} from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RecurrentType } from 'modules/project_management/components/Schedule/RecurrentTypeModal/RecurrentTypeSelector'

import DeleteConfirmationModal from './DeleteConfirmationModal'
import { ProjectHoursAssignationForm } from './ProjectHoursAssignationForm'

const DEFAULT_WEEK_DAYS = [1, 2, 3, 4, 5, 6, 0]

export interface Props {
  open: boolean
  onClose: () => void
  onDateChange: (startDate: string) => void
  id?: string
  dailyMinutes?: number
  startDate?: string
  endDate?: string
  projectWorkerIds?: string[]
  project?: ProjectForSelectFragment
  subproject?: SubprojectForSelectFragment
  employee?: EmployeeForSelectFragmentNoAccess | null
  weekDays?: number[]
  recurrentType?: RecurrentType
}

export const ScheduleSidepanel = ({
  open,
  onClose,
  onDateChange,
  id,
  dailyMinutes,
  startDate,
  endDate,
  projectWorkerIds,
  project,
  subproject,
  employee,
  weekDays,
  recurrentType = 'all',
}: Props) => {
  const defaultData = {
    dailyMinutes: dailyMinutes ?? 0,
    endDate: endDate ? dateBackendToUI(endDate) : '',
    projectWorkerIds: projectWorkerIds ?? [],
    startDate: startDate ? dateBackendToUI(startDate) : '',
    project: project,
    subproject: subproject,
    employee: employee,
    weekDays: weekDays ?? DEFAULT_WEEK_DAYS,
  }

  const [data, setData] =
    useState<BulkCreateProjectManagementPlannedRecordMutationVariables>(defaultData)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)

  const handleDelete = () => {
    if (recurrentType === 'custom') {
      onSubmitDeleteCustomRange()
    } else {
      onSubmitDelete()
    }
    setShowDeleteConfirmationModal(false)
  }

  const validPlannedRecord = () => {
    return !!(
      data?.projectWorkerIds?.length &&
      data?.startDate &&
      data?.endDate &&
      data?.dailyMinutes
    )
  }

  const [bulkCreatePlannedRecords, { loading: createLoading }] = useMutation(
    BulkCreateProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.bulkCreateProjectmanagementPlannedRecord,
    }
  )

  const [deletePlannedRecord, { loading: deleteLoading }] = useMutation(
    DeleteProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.deleteProjectmanagementPlannedRecord,
    }
  )

  const [deleteCustomRangePlannedRecord, { loading: deleteCustomRangeLoading }] = useMutation(
    DeleteCustomRangeProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.deleteCustomRangeProjectmanagementPlannedRecord,
    }
  )

  const [updatePlannedRecord, { loading: updateLoading }] = useMutation(
    UpdateProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.updateProjectmanagementPlannedRecord,
    }
  )

  const [updateCustomRangePlannedRecord, { loading: updateCustomRangeLoading }] = useMutation(
    UpdateCustomRangeProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.updateCustomRangeProjectmanagementPlannedRecord,
    }
  )

  const onChange = (value: BulkCreateProjectManagementPlannedRecordMutationVariables | null) => {
    if (value) {
      if (value.startDate !== data?.startDate) {
        onDateChange(value.startDate)
      }

      setData(value)
    }
  }

  const handleSuccess = () => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.saved_successfully'),
      time: 2000,
    })
    onClose()
  }

  const handleError = (errors: readonly ErrorDetailsFragment[]) => {
    const any_validation_error = errors.some(
      (error) => 'type' in error && error.type === 'validation_failed'
    )
    if (any_validation_error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('project_management.schedule.sidepanel.errors.validation'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
        time: 2000,
      })
    }
  }

  const onSubmitCreate = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnSaveInModal, {})
    const result = await bulkCreatePlannedRecords({
      variables: {
        dailyMinutes: data.dailyMinutes,
        endDate: dateUIToBackend(data.endDate),
        projectWorkerIds: data.projectWorkerIds,
        startDate: dateUIToBackend(data.startDate),
        subprojectId: data.subprojectId,
        weekDays: data.weekDays,
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      handleSuccess()
    } else {
      handleError(handled.errors)
    }
  }

  const onSubmitUpdate = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnUpdateInModal, {})
    if (!id) return
    const result = await updatePlannedRecord({
      variables: {
        id: id,
        dailyMinutes: data.dailyMinutes,
        endDate: dateUIToBackend(data.endDate),
        startDate: dateUIToBackend(data.startDate),
        projectWorkerId: data.projectWorkerIds[0],
        weekDays: data.weekDays,
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      handleSuccess()
    } else {
      handleError(handled.errors)
    }
  }

  const onSubmitUpdateCustomRange = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnUpdateInModal, {})
    if (!id) return
    const result = await updateCustomRangePlannedRecord({
      variables: {
        id: id,
        dailyMinutes: data.dailyMinutes,
        endDate: dateUIToBackend(data.endDate),
        startDate: dateUIToBackend(data.startDate),
        projectWorkerId: data.projectWorkerIds[0],
        weekDays: data.weekDays,
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      handleSuccess()
    } else {
      handleError(handled.errors)
    }
  }

  const onSubmitDelete = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnDeleteInModal, {})
    if (!id) return
    const result = await deletePlannedRecord({
      variables: {
        id: id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      handleSuccess()
    } else {
      handleError(handled.errors)
    }
  }

  const onSubmitDeleteCustomRange = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnDeleteInModal, {})
    if (!id) return
    const result = await deleteCustomRangePlannedRecord({
      variables: {
        id: id,
        endDate: dateUIToBackend(data.endDate),
        startDate: dateUIToBackend(data.startDate),
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      handleSuccess()
    } else {
      handleError(handled.errors)
    }
  }

  const loading =
    createLoading ||
    deleteLoading ||
    updateLoading ||
    updateCustomRangeLoading ||
    deleteCustomRangeLoading
  const isDisabled = !validPlannedRecord() || loading

  const handleClose = () => {
    track(trackingEvents.projectManagement.schedule.clickOnCancelInModal, {})
    onClose()
  }

  return (
    <>
      <Sidepanel
        width="narrow"
        title={i18n.t('project_management.schedule.sidepanel.assign_hours')}
        open={open}
        onClose={handleClose}
        closeLabel={i18n.t('common.close')}
        footer={
          <>
            <Button onClick={onClose} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            {!id ? (
              <Button disabled={isDisabled} onClick={onSubmitCreate} type="primary">
                {i18n.t('common.save')}
              </Button>
            ) : (
              <>
                <Button
                  disabled={isDisabled}
                  onClick={() => setShowDeleteConfirmationModal(true)}
                  type="secondary"
                >
                  {i18n.t('common.delete')}
                </Button>
                {recurrentType === 'custom' ? (
                  <Button disabled={isDisabled} onClick={onSubmitUpdateCustomRange} type="primary">
                    {i18n.t('common.update')}
                  </Button>
                ) : (
                  <Button disabled={isDisabled} onClick={onSubmitUpdate} type="primary">
                    {i18n.t('common.update')}
                  </Button>
                )}
              </>
            )}
          </>
        }
      >
        <ProjectHoursAssignationForm
          plannedRecord={data}
          onChange={onChange}
          existingId={id}
          project={project}
          subproject={subproject}
          employee={employee}
        />
        {loading && (
          <Box height="full" justifyContent="center" alignItems="center">
            <Spinner />
          </Box>
        )}
      </Sidepanel>
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          onClose={() => setShowDeleteConfirmationModal(false)}
          onSubmit={handleDelete}
        />
      )}
    </>
  )
}
